import { useEffect, useState } from "react";
import { toast } from "sonner";
import AppShell from "@/pages/AppShell";
import { v1 } from "@/lib/api";
import { useOrg, hasRole } from "@/lib/orgContext";
import QuotaInline from "@/components/QuotaInline";
import { Folder, Plus, Trash } from "iconoir-react";

export default function Projects() {
  const { activeOrgId, activeOrg, activeRole } = useOrg();
  const canCreate = hasRole(activeRole, "manager");
  const canDelete = hasRole(activeRole, "admin");

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    if (!activeOrgId) {
      setProjects([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await v1.projects.list(activeOrgId);
      setProjects(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !domain.trim() || !activeOrgId) return;
    setCreating(true);
    try {
      await v1.projects.create(activeOrgId, { name: name.trim(), primary_domain: domain.trim() });
      toast.success("Project created");
      setName(""); setDomain("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, isDefault) => {
    if (isDefault) {
      toast.error("Cannot delete the default project");
      return;
    }
    if (!confirm("Delete this project?")) return;
    try {
      await v1.projects.remove(id);
      toast.success("Project deleted");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete project");
    }
  };

  return (
    <AppShell title="Projects" subtitle={activeOrg?.name || "WORKSPACE"}>
      <QuotaInline resource="projects" />
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3" data-testid="projects-list">
          {!activeOrgId && (
            <div className="border border-border p-6 text-sm text-muted-foreground">
              No active organization. Create or switch to one in the header.
            </div>
          )}
          {activeOrgId && loading && <div className="font-mono text-xs text-muted-foreground">Loading…</div>}
          {activeOrgId && !loading && projects.length === 0 && (
            <div className="border border-border p-6 text-sm text-muted-foreground">No projects yet.</div>
          )}
          {projects.map((p) => (
            <div
              key={p.id}
              data-testid={`project-card-${p.slug}`}
              className="border border-border p-5 flex items-center justify-between"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-10 h-10 border border-border flex items-center justify-center">
                  <Folder width={18} height={18} strokeWidth={1.6} />
                </div>
                <div className="min-w-0">
                  <div className="font-app font-medium truncate">{p.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {p.primary_domain} {p.is_default && "· default"}
                  </div>
                </div>
              </div>
              {canDelete && !p.is_default && (
                <button
                  onClick={() => handleDelete(p.id, p.is_default)}
                  data-testid={`project-delete-${p.slug}`}
                  className="p-1.5 border border-border hover:bg-card"
                  title="Delete"
                >
                  <Trash width={14} height={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        {activeOrgId && canCreate && (
          <form
            onSubmit={handleCreate}
            data-testid="project-create-form"
            className="border border-border p-5 space-y-3 h-fit"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Create project</div>
            <input
              type="text"
              placeholder="Project name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              minLength={2}
              maxLength={80}
              data-testid="project-create-name"
              className="w-full border border-border px-3 py-2 bg-background text-sm font-app"
            />
            <input
              type="text"
              placeholder="primary domain (e.g. acme.com)"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              required
              minLength={3}
              data-testid="project-create-domain"
              className="w-full border border-border px-3 py-2 bg-background text-sm font-app"
            />
            <button
              type="submit"
              disabled={creating}
              data-testid="project-create-submit"
              className="w-full flex items-center justify-center gap-2 py-2 bg-obsidian text-bone text-xs font-app uppercase tracking-widest disabled:opacity-60"
            >
              <Plus width={14} height={14} /> {creating ? "Creating…" : "Create project"}
            </button>
          </form>
        )}
      </div>
    </AppShell>
  );
}
